import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";

import { formatGameTime } from "@/features/analysis";
import { formatPercent } from "@/features/matches";
import { cn } from "@/shared/lib";

import { useDrillAssets } from "../../api/useDrillAssets";
import { fullAnswer } from "../../model/draftAnswer";
import type { DrillAttempt, DrillSpec } from "../../model/Drill";
import { formatNumber } from "../../model/formatNumber";
import { orderedPairs } from "../../model/orderedPairs";
import { AssetImage } from "./AssetImage";
import { OrderTable } from "./OrderTable";
import { ShareBars } from "./ShareBars";
import { type SliderMark, SliderMarks } from "./SliderMarks";
import { useOptionView } from "./useOptionView";

const PICK_RATE = msg({ message: "How often strong players on {hero} buy each item" });
const DEFENSE_CAPTION = msg({
  message:
    "Defense against {kind, select, bullet {bullets} spirit {spirit} other {melee}} among strong players on {hero}",
});
const ORDER_VERDICT = msg({
  message:
    "{grade, select, right {Right: all pairs in order.} close {Close: {hits} of {total} pairs in order.} other {{hits} of {total} pairs in order.}}",
});
const MINE_BOUGHT = msg({ message: "In the match you bought {item} at {time}." });
const ODD_BOUGHT = msg({
  message: "You bought {item} at {time}: strong players take it this early in {share} of matches.",
});
const PURCHASE_USUAL = msg({
  message: "Strong players buy {item} around {usual}; you bought it at {time}.",
});
const LATE_BY = msg({
  message:
    "You got it at {time}, {minutes, plural, one {# minute} other {# minutes}} later than strong players.",
});
const BOUGHT_AT = msg({ message: "You got it at {time}." });
const ITEM_LATE = msg({
  message: "You got {item} {minutes, plural, one {# minute} other {# minutes}} later.",
});
const ITEM_NOT_BOUGHT = msg({ message: "You didn't buy {item}." });
const PAIR_SHARES = msg({ message: "{share} · usually {usual}" });
const UPLIFT = msg({ message: "{times}× more often than usual" });
const TOLERANCE = msg({ message: "Exact is within {exact}, close is within {close}." });

// Опоздание на столько и больше — «позже сильных»; меньше — купил вовремя.
const LATE_S = 60;

type DrillRevealProps = { spec: DrillSpec; attempt: DrillAttempt };

// Разбор ответа: вердикт, одна картинка (полосы долей, верный порядок, шкала) и не больше
// одной строки. Пояснения к вариантам — пометками на самих вариантах, на чём основан ответ —
// одной строкой внизу экрана.
export const DrillReveal = ({ spec, attempt }: DrillRevealProps) => {
  const { i18n, t } = useLingui();
  const assets = useDrillAssets();
  const view = useOptionView();
  const hero = assets.hero(spec.hero_id).name;
  const answer = fullAnswer(attempt.answer);
  const correct = spec.options.find((option) => spec.correct_ids.includes(option.id));
  let verdict = {
    right: t`Right!`,
    close: t`Close: counts as half.`,
    wrong: t`Not quite.`,
  }[attempt.grade];
  if (spec.mechanic === "order") {
    const { hits, total } = orderedPairs(spec.correct_ids, answer.option_ids);
    verdict = i18n._({ ...ORDER_VERDICT, values: { grade: attempt.grade, hits, total } });
  }

  const line = (text: string, bad = false) => (
    <p className={cn("text-sm", bad ? "text-bad" : "text-foreground-soft")}>{text}</p>
  );

  const bars = () => (
    <ShareBars
      caption={i18n._({ ...PICK_RATE, values: { hero } })}
      bars={spec.options.map((option) => ({
        id: option.id,
        name: view(option).name,
        share: option.value ?? 0,
        correct: spec.correct_ids.includes(option.id),
        close: spec.close_ids.includes(option.id),
      }))}
    />
  );

  // Защита: доли только среди защиты от этого урона и когда ответ купил игрок. У задач,
  // созданных до появления `references`, — доли вариантов, как раньше.
  const defense = () => {
    if (spec.references.length === 0) {
      return bars();
    }
    const typical = spec.typical_value;
    const mine = spec.mine_value;
    let timing: ReactNode = null;
    if (mine == null) {
      timing = line(t`You didn't buy it in the match.`, true);
    } else if (typical != null && mine - typical >= LATE_S) {
      const minutes = Math.round((mine - typical) / 60);
      timing = line(i18n._({ ...LATE_BY, values: { time: formatGameTime(mine), minutes } }), true);
    } else {
      timing = line(i18n._({ ...BOUGHT_AT, values: { time: formatGameTime(mine) } }));
    }
    return (
      <>
        <ShareBars
          caption={i18n._({
            ...DEFENSE_CAPTION,
            values: { kind: spec.damage_kind ?? "melee", hero },
          })}
          bars={spec.references.map((option) => ({
            id: option.id,
            name: view(option).name,
            share: option.value ?? 0,
            correct: spec.correct_ids.includes(option.id),
            time: option.time_s == null ? null : formatGameTime(option.time_s),
          }))}
        />
        {timing}
      </>
    );
  };

  const timeMarks = () => {
    const marks: SliderMark[] = [];
    if (spec.answer_value != null) {
      marks.push({
        label: t`Right answer`,
        value: spec.answer_value,
        text: formatGameTime(spec.answer_value),
        tone: "good",
      });
    }
    if (answer.value != null) {
      marks.push({
        label: t`Your answer`,
        value: answer.value,
        text: formatGameTime(answer.value),
        tone: "accent",
      });
    }
    if (spec.mine_value != null) {
      marks.push({
        label: t`You in the match`,
        value: spec.mine_value,
        text: formatGameTime(spec.mine_value),
        tone: "neutral",
      });
    }
    return (
      <>
        <SliderMarks min={spec.min_value ?? 0} max={spec.max_value ?? 0} marks={marks} />
        {spec.exact_tolerance != null && spec.close_tolerance != null && (
          <span className="text-[13px] text-subtle">
            {i18n._({
              ...TOLERANCE,
              values: {
                exact: formatGameTime(spec.exact_tolerance),
                close: formatGameTime(spec.close_tolerance),
              },
            })}
          </span>
        )}
      </>
    );
  };

  const lateItem = () => {
    if (spec.target_item_id == null) {
      return null;
    }
    const item = assets.item(spec.target_item_id).name;
    const mine = spec.mine_value;
    const typical = spec.typical_value;
    if (mine == null) {
      return line(i18n._({ ...ITEM_NOT_BOUGHT, values: { item } }), true);
    }
    if (typical != null && mine - typical >= LATE_S) {
      const minutes = Math.round((mine - typical) / 60);
      return line(i18n._({ ...ITEM_LATE, values: { item, minutes } }), true);
    }
    return null;
  };

  let details: ReactNode = null;
  switch (spec.kind) {
    case "buy_now": {
      const mine = spec.options.find((option) => option.mine);
      details = (
        <>
          {bars()}
          {mine?.item_id != null &&
            line(
              i18n._({
                ...MINE_BOUGHT,
                values: {
                  item: assets.item(mine.item_id).name,
                  time: formatGameTime(spec.moment_s ?? 0),
                },
              }),
            )}
        </>
      );
      break;
    }
    case "odd_item":
      details = (
        <>
          {bars()}
          {correct?.item_id != null &&
            line(
              i18n._({
                ...ODD_BOUGHT,
                values: {
                  item: assets.item(correct.item_id).name,
                  time: formatGameTime(spec.moment_s ?? 0),
                  share: formatPercent(correct.value ?? 0, i18n.locale),
                },
              }),
            )}
        </>
      );
      break;
    case "anti_heal":
      details = bars();
      break;
    case "defense_item":
      details = defense();
      break;
    case "killer_counters":
      // Контрпредметы — доли на самих вариантах; без них задача спрашивала защиту.
      details = spec.damage_kind == null ? null : defense();
      break;
    case "finish_him":
      details = null;
      break;
    case "counter_pairs":
      details = (
        <ul className="flex flex-col gap-2 text-sm">
          {spec.pairs.map((pair) => {
            const left = spec.options.find((option) => option.id === pair.left_id);
            const right = spec.options.find((option) => option.id === pair.right_id);
            if (left === undefined || right === undefined) {
              return null;
            }
            const enemy = view(left);
            const item = view(right);
            const shares =
              right.pick_rate != null && right.usual_pick_rate != null
                ? i18n._({
                    ...PAIR_SHARES,
                    values: {
                      share: formatPercent(right.pick_rate, i18n.locale),
                      usual: formatPercent(right.usual_pick_rate, i18n.locale),
                    },
                  })
                : i18n._({
                    ...UPLIFT,
                    values: {
                      times: formatNumber(right.value ?? 1, i18n.locale, {
                        maximumFractionDigits: 1,
                      }),
                    },
                  });
            return (
              <li key={pair.left_id} className="flex flex-wrap items-center gap-2">
                {enemy.asset !== null && <AssetImage asset={enemy.asset} size={28} />}
                <span>{enemy.name}</span>
                <span aria-hidden="true" className="text-subtle">
                  →
                </span>
                {item.asset !== null && <AssetImage asset={item.asset} size={28} />}
                <span className="font-semibold">{item.name}</span>
                <span className="font-mono text-muted">{shares}</span>
              </li>
            );
          })}
        </ul>
      );
      break;
    case "build_order":
      details = (
        <>
          <OrderTable
            spec={spec}
            answer={answer}
            nameLabel={t`Item`}
            typicalLabel={t`Strong players`}
            mineLabel={t`You in the match`}
            format={formatGameTime}
          />
          {lateItem()}
        </>
      );
      break;
    case "buy_minute":
    case "first_buff":
      details = timeMarks();
      break;
    case "purchase_mistake":
      details =
        correct?.item_id == null || spec.typical_value == null
          ? null
          : line(
              i18n._({
                ...PURCHASE_USUAL,
                values: {
                  item: assets.item(correct.item_id).name,
                  usual: formatGameTime(spec.typical_value),
                  time: formatGameTime(correct.value ?? 0),
                },
              }),
            );
      break;
  }

  return (
    <section
      aria-live="polite"
      className={cn(
        "flex flex-col gap-3.5 rounded-xl border px-5 py-4",
        attempt.grade === "right" && "border-good/60 bg-good/10",
        attempt.grade === "close" && "border-warn/60 bg-warn/10",
        attempt.grade === "wrong" && "border-bad/60 bg-bad/10",
      )}
    >
      <p
        className={cn(
          "text-lg font-semibold",
          attempt.grade === "right" && "text-good",
          attempt.grade === "close" && "text-warn",
          attempt.grade === "wrong" && "text-bad",
        )}
      >
        {verdict}
      </p>
      {details}
    </section>
  );
};
